import {DataGrid, GridColDef, GridFilterItem} from "@material-ui/data-grid";
import React, {FC} from "react";
import {MoreVert} from "@material-ui/icons";
import {useHistory} from "react-router-dom";
import {IconButton} from "@material-ui/core";
import {OrdersReloadButton} from "./OrdersReloadButton";
import {ClientDisplay} from "../../clients/sharedComponents/ClientDisplay";
import {FeOrder} from "../redux/types";
import {tableLocalizations} from "../../../sharedComponents/tableLocalizations";
import {TableToolbar} from "../../../sharedComponents/TableToolbar";
import {WorkerDisplay} from "../../workers/sharedComponents/WorkerDisplay";
import {format} from "date-fns";
import {ClientGridFilterInput} from "../../clients/sharedComponents/ClientAutoComplete";
import {WorkerGridFilterInput} from "../../workers/sharedComponents/WorkerAutoComplete";
import {getServicesTableCell} from "../../../sharedComponents/ServicesTableCell";
import {getOrderStatusProps, OrderStatusChip, statusMap} from "../sharedComponents/OrderStatusChip";
import {store} from "../../../redux/store";
import {selectClientById} from "../../clients/redux/clients.slice";
import {getFullName} from "../../../utils";
import {selectWorkerById} from "../../workers/redux/workers.slice";
import {formatDate} from "../../../date.utils";
import {OrderStatusFilterInput} from "./OrderStatusFilterInput";
import {GreenCheckIcon} from "../../../sharedComponents/GreenCheckIcon";
import {RedCloseIcon} from "../../../sharedComponents/RedCloseIcon";

const columns: GridColDef[] = [
    {
        field: 'id',
        hide: true
    },
    {
        field: 'Menu',
        disableColumnMenu: true,
        editable: false,
        resizable: false,
        width: 80,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderHeader: () => <OrdersReloadButton/>,
        renderCell: () => <IconButton><MoreVert/></IconButton>
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 170,
        valueGetter: (params) => getOrderStatusProps(params.row as FeOrder).name,
        renderCell: (params) => (<OrderStatusChip chipProps={{size: "small"}} order={params.row as FeOrder}/>),
        filterOperators: [
            {
                label: 'ist',
                value: 'status',
                getApplyFilterFn: (filterItem: GridFilterItem) => {
                    if (
                        !filterItem.columnField ||
                        !filterItem.value ||
                        !filterItem.operatorValue
                    ) {
                        return null;
                    }

                    return (params): boolean => {
                        const orderStatus = getOrderStatusProps(params.row as FeOrder).name;
                        const statusFilter = filterItem.value as string;
                        return ! statusFilter || statusFilter === "-" || orderStatus === statusMap[statusFilter].name;
                    };
                },
                InputComponent: OrderStatusFilterInput
            }
        ]
    },
    {
        field: 'clientId',
        headerName: 'Kunde',
        filterable: true,
        width: 220,
        valueFormatter: (params) => {
            const client = selectClientById(store.getState(), params.row.clientId);
            return client ? getFullName(client) : ""
        },
        renderCell: (params) => <ClientDisplay clientId={params.row.clientId as string}/>,
        filterOperators: [
            {
                label: "ist",
                value: "clientId",
                getApplyFilterFn: (filterItem: GridFilterItem) => {
                    if (
                        !filterItem.columnField ||
                        !filterItem.value ||
                        !filterItem.operatorValue
                    ) {
                        return null;
                    }

                    return (params): boolean => {
                        const clientId = params.value as string;
                        const searchedClientId = filterItem.value as string;
                        return searchedClientId === clientId;
                    };
                },
                InputComponent: ClientGridFilterInput,
            }
        ]
    },
    {
        field: 'workerId',
        headerName: 'Gartenarbeiter',
        width: 220,
        valueFormatter: (params) => {
            const client = selectWorkerById(store.getState(), params.row.workerId);
            return client ? getFullName(client) : ""
        },
        renderCell: (params) => <WorkerDisplay workerId={params.row.workerId as string}/>,
        filterOperators: [
            {
                label: "ist",
                value: "workerId",
                getApplyFilterFn: (filterItem: GridFilterItem) => {
                    if (
                        !filterItem.columnField ||
                        !filterItem.value ||
                        !filterItem.operatorValue
                    ) {
                        return null;
                    }

                    return (params): boolean => {
                        const workerId = params.value as string;
                        const searchedWorkerId = filterItem.value as string;
                        return searchedWorkerId === workerId;
                    };
                },
                InputComponent: WorkerGridFilterInput,
            }
        ]
    },
    {
        field: 'estimatedFrom',
        headerName: 'Geschätzt von',
        width: 170,
        type: "date",
        valueFormatter: (params) => formatDate(params.value as number, "dd.MM.yyyy HH:mm"),
        renderCell: (param) => format(param.value as number, "dd.MM.yyyy - HH:mm")
    },
    {
        field: 'estimatedTo',
        headerName: 'Geschätzt bis',
        width: 170,
        type: "date",
        valueFormatter: (params) => formatDate(params.value as number, "dd.MM.yyyy HH:mm"),
        renderCell: (param) => format(param.value as number, "dd.MM.yyyy - HH:mm")
    },
    {
        hide: true,
        field: 'from',
        headerName: 'Stattgefunden von',
        width: 200,
        type: "date",
        valueFormatter: (params) => params.value && formatDate(params.value as number, "dd.MM.yyyy HH:mm"),
        renderCell: (param) => param.value && format(param.value as number, "dd.MM.yyyy - HH:mm")
    },
    {
        hide: true,
        field: 'to',
        headerName: 'Stattgefunden bis',
        width: 200,
        type: "date",
        valueFormatter: (params) => params.value && formatDate(params.value as number, "dd.MM.yyyy HH:mm"),
        renderCell: (param) => param.value && format(param.value as number, "dd.MM.yyyy - HH:mm")
    },
    {
        field: 'billed',
        type: 'boolean',
        headerName: 'Abgerechnet',
        align: "center",
        width: 160,
        renderCell: (params) => params.value === true ? <GreenCheckIcon/> : <RedCloseIcon/>,
        valueFormatter: (params) => params.value === true ? "ja" : "nein"
    },
    {...getServicesTableCell(true)}
]

export const OrdersTable: FC<{ orders: FeOrder[], loading: boolean }> = ({orders, loading}) => {
    const history = useHistory();
    return (
        <div style={{height: 710, width: '100%'}}>
            <DataGrid
                localeText={tableLocalizations}
                components={{
                    Toolbar: TableToolbar
                }}
                onRowClick={(param) => history.push(`/admin/orders/order/${param.id}`)}
                loading={loading}
                rows={orders}
                disableMultipleSelection={true}
                columns={columns}
                isCellEditable={() => false}
                pageSize={10}
                checkboxSelection={false}
                disableSelectionOnClick
            />
        </div>
    )
}
